import { getAPI, postAPI } from '../utils/ApiFunctions';
import MapboxLogic from '../utils/mapboxLogic';
import {
    BENEFITS_SERVICE_ERROR_CODES,
    ERROR_CODE_HEADER,
    ERROR_MESSAGE_HEADER,
    MICRO_FRONTEND_STARGATE_KEY,
} from '../constants/index';
import { StatusCodes } from 'http-status-codes';
import { getMicrofrontendStargate } from '../utils/microFrontendStargateToken';
import {
    getMemberBenefitsFamilyStatusIndicator,
    getTciTableNumberFromPlanBenefits,
    determineDdpLabBenefits,
    determineDdpRadiologyBenefits,
} from '../utils/PlanBenefitsUtil';

const genericErrorResponse = {
    memberDetails: null,
    errorMessage: 'An error occurred, please try again later.',
};

/**
 * Gets the member demographic & eligibility information from the Benefits service
 */
const getMemberDetails = async (formValues) => {
    const { firstName, lastName, groupNumber, memberNumber, dateOfBirth } = formValues;
    const microFrontendToken = await getMicrofrontendStargate(MICRO_FRONTEND_STARGATE_KEY);

    // get updated member-details url based on microfrontend feature-flag if its available
    const updatedUrl = getUpdatedUrl();

    // Retrieve member details from benefits service
    const response = await postAPI(
        updatedUrl ? updatedUrl : process.env.MEMBER_DETAILS_ENDPOINT,
        {
            firstName: firstName.value.trim(),
            lastName: lastName.value.trim(),
            groupNumber: groupNumber.truncated,
            memberId: memberNumber.value.trim(),
            dob: dateOfBirth.value.trim(),
        },
        null,
        null,
        microFrontendToken
    );

    let benefitsServiceResponse;
    let memberDetails;
    let errorMessage;

    try {
        // check for error response (500) from Benefits Service - log & return
        if (response instanceof Error) {
            console.error(
                `Call to Benefits Service getMemberDetails endpoint failed with error ${response.message}`
            );
            return genericErrorResponse;
        }

        // check for no content - log & return
        if (response.status === StatusCodes.NO_CONTENT) {
            errorMessage = determineNoContentError(response.headers);
            return { memberDetails, errorMessage };
        }

        // check for partial content - log & determine whether to return or continue
        if (response.status === StatusCodes.PARTIAL_CONTENT) {
            errorMessage = determinePartialContentError(response.headers);
            if (errorMessage) {
                return { memberDetails, errorMessage };
            }
        }

        benefitsServiceResponse = response.data;

        memberDetails = await gatherAdditionalMemberDetails(benefitsServiceResponse, formValues);

        // make this call to get/store in redis cache the member's benefit/plan details info which will be needed in orchestration
        const planBenefits = callMemberPlanBenefitEndpoint(
            memberDetails,
            formValues,
            response.headers['x-rally-correlationid']
        );

        return {
            memberDetails,
            errorMessage: null,
            cpttCorrelationId: response.headers['x-rally-correlationid'],
            planBenefits,
        };
    } catch (error) {
        console.error(error.message);
        return genericErrorResponse;
    }
};

// gather additional member details based on benefits service response
const gatherAdditionalMemberDetails = async (benefitsServiceResponse, formValues) => {
    const { memberNumber } = formValues;
    const popConfig = benefitsServiceResponse.popConfig;

    const { subscriberId, alternateId } = await getSubscriberIdForMember(
        benefitsServiceResponse,
        memberNumber.value
    );

    let locationOfService;
    try {
        const mapboxAddressResponse = await MapboxLogic.getMapboxAddressResponse(
            getInputAddress(benefitsServiceResponse)
        );
        // Pre-populate member permanent address as location of service
        locationOfService = mapboxAddressResponse.data.features[0];
    } catch (error) {
        console.error(`Error communicating with Mapbox: ${error}`);
    }

    let lineOfBusiness = benefitsServiceResponse.lineOfBusiness;
    // TODO get rid of this when cost calc acknowledges E&I, C&S, etc. as valid entities
    if (['E&I', 'C&S'].includes(benefitsServiceResponse?.lineOfBusiness)) {
        lineOfBusiness = benefitsServiceResponse.lineOfBusiness.replace(/&/g, 'N');
    }

    return {
        benefitsServiceResponse,
        popConfig,
        subscriberId,
        alternateId,
        locationOfService,
        lineOfBusiness,
        cae: popConfig?.cae ?? '',
        sourceCode: benefitsServiceResponse.sourceCode,
        policyNumber: benefitsServiceResponse.policyNumber,
        formValues,
    };
};

const callMemberPlanBenefitEndpoint = async (memberDetails, formValues, requestId) => {
    try {
        const benefitsRequest = {
            firstName: memberDetails?.benefitsServiceResponse?.demographics?.name?.firstName,
            lastName: memberDetails?.benefitsServiceResponse?.demographics?.name?.lastName,
            memberId: formValues.memberNumber.value.trim(),
            groupNumber: formValues.groupNumber.truncated,
            dob: memberDetails?.benefitsServiceResponse?.demographics?.dateOfBirth,
            payer: memberDetails?.popConfig?.payer,
            portalSource: memberDetails?.popConfig?.portalSource,
        };
        const microFrontEndToken = await getMicrofrontendStargate(MICRO_FRONTEND_STARGATE_KEY);

        const response = await postAPI(
            process.env.MEMBER_PLAN_BENEFITS_ENDPOINT,
            benefitsRequest,
            requestId,
            null,
            microFrontEndToken
        );
        // check for error response (500 or 404) from Benefits Service - log & return
        if (response instanceof Error) {
            console.error(
                `Failed to obtain member plan benefit information with error ${response.message}`
            );
        } else {
            return {
                nonEmbeddedFamilyStatusIndicator: getMemberBenefitsFamilyStatusIndicator(response),
                tciTableNumber: getTciTableNumberFromPlanBenefits(response),
                hasDdpLabBenefits: determineDdpLabBenefits(response),
                hasDdpRadiologyBenefits: determineDdpRadiologyBenefits(response),
            };
        }
    } catch (error) {
        console.error(
            'Error occurred when trying to call member plan /benefits endpoint ',
            error.message
        );
    }
};

/**
 * When Benefit Service returns with a 204 No Content Status,
 * determine what error occured based on the response headers, set error message to be displayed accordingly
 * @param {object} headers Ben Service response headers that include x-epe-error-code header, a comma delimited list of error codes
 * @returns {string} errorMessage to be displayed in the error Snackbar that appears on Member Page
 */
const determineNoContentError = (headers) => {
    const errorCodes = headers?.[ERROR_CODE_HEADER];
    console.error(headers?.[ERROR_MESSAGE_HEADER]);
    let errorMessage = genericErrorResponse.errorMessage;
    if (errorCodes) {
        const errorCodesArray = errorCodes.split(',');
        BENEFITS_SERVICE_ERROR_CODES.MEMBER_NOT_FOUND.code.forEach((code) => {
            if (errorCodesArray?.includes(code)) {
                errorMessage = BENEFITS_SERVICE_ERROR_CODES.MEMBER_NOT_FOUND.displayMessage;
            }
        });
        BENEFITS_SERVICE_ERROR_CODES.NO_ACTIVE_COVERAGE.code.forEach((code) => {
            if (errorCodesArray?.includes(code)) {
                errorMessage = BENEFITS_SERVICE_ERROR_CODES.NO_ACTIVE_COVERAGE.displayMessage;
            }
        });
    }
    return errorMessage;
};

/**
 * When Benefit Service returns with a 206 Partial Conentt Status, determine what error occured based on the response headers
 * If it is a "breaking" error, i.e. we should not let member continue, set error message and set boolean to return with error
 * @param {object} headers Ben Service response headers that include x-epe-error-code header, a comma delimited list of error codes
 * @returns {string} errorMessage (if applicable) to be displayed in error Snackbar that appears on Member Page, else null
 */
const determinePartialContentError = (headers) => {
    const errorCodes = headers?.[ERROR_CODE_HEADER];
    console.error(headers?.[ERROR_MESSAGE_HEADER]);
    let errorMessage;
    if (errorCodes) {
        const errorCodesArray = errorCodes.split(',');
        BENEFITS_SERVICE_ERROR_CODES.MEMBER_NOT_SUPPORTED.code.forEach((code) => {
            if (errorCodesArray?.includes(code)) {
                errorMessage = BENEFITS_SERVICE_ERROR_CODES.MEMBER_NOT_SUPPORTED.displayMessage;
            }
        });
    }

    return errorMessage;
};

/**
 * Gets subscriber id from medical or BH coverage info in profile details, default to member id
 * set value for subscriber Id in context so it can be accessed later during fulfillment
 * @param {obj} benefitsServiceResponse member profile details returned from ben service, contains coverage info
 * @param {string} memberNumber the big5 member number/id, subscriberID will default to this if we can't get it from cov
 */
const getSubscriberIdForMember = (benefitsServiceResponse, memberNumber) => {
    const popConfig = benefitsServiceResponse.popConfig;
    let alternateId = memberNumber;
    let subscriberId = memberNumber;
    const allHealthCoverages = [];

    if (benefitsServiceResponse?.eligibility?.length > 0) {
        benefitsServiceResponse.eligibility.map((eligibility) => {
            allHealthCoverages.push(...eligibility.memberHealthCoverage.coverageType);
        });
    }
    if (allHealthCoverages.length > 0) {
        let healthCoverageIndex = allHealthCoverages.findIndex(
            (coverage) => coverage.typeCode.desc === 'MEDICAL'
        );

        // couldn't find medical coverage, check for behavioral health coverage
        if (healthCoverageIndex < 0) {
            healthCoverageIndex = allHealthCoverages.findIndex(
                (coverage) => coverage.typeCode.desc === 'BEHAVIORAL HEALTH'
            );
        }
        // if the CAE is PH_ACCENT, use 'PHYSICAL CARVE-OUT' coverage type
        if (popConfig && popConfig.cae === 'PH_ACCENT') {
            healthCoverageIndex = allHealthCoverages.findIndex(
                (coverage) => coverage.typeCode.desc === 'PHYSICAL CARVE-OUT'
            );
        }
        // if the CAE is COSMOS/OBH_FACETS, use 'BEHAVIORAL HEALTH' or 'BEHAVIORAL CARVE-OUT' coverage type
        else if (popConfig && (popConfig.cae === 'COSMOS' || popConfig.cae === 'OBH_FACETS')) {
            healthCoverageIndex = allHealthCoverages.findIndex(
                (coverage) => coverage.typeCode.desc === 'BEHAVIORAL HEALTH'
            );
            if (healthCoverageIndex < 0) {
                healthCoverageIndex = allHealthCoverages.findIndex(
                    (coverage) => coverage.typeCode.desc === 'BEHAVIORAL CARVE-OUT'
                );
            }
        }
        // if we find either medical or behavioral health coverage, pull subscriber id from coverage info
        if (healthCoverageIndex >= 0) {
            const coverageIds = allHealthCoverages[healthCoverageIndex].coverageID;
            let subscriberIdIndex = coverageIds.findIndex(
                (coverage) => coverage.sourceSysCode.toUpperCase() === 'SUBSCRIBER ID'
            );
            subscriberId =
                subscriberIdIndex >= 0 ? coverageIds[subscriberIdIndex].id : memberNumber;

            // if the CAE is UNET/COSMOS, then get the alternateID from coverage
            if (popConfig && (popConfig?.cae === 'UNET' || popConfig?.cae === 'COSMOS')) {
                let alternateIdIndex = coverageIds.findIndex(
                    (coverage) => coverage.sourceSysCode.toUpperCase() === 'ALTERNATE ID'
                );
                if (alternateIdIndex >= 0) {
                    subscriberId = coverageIds[alternateIdIndex].id;
                    alternateId = coverageIds[alternateIdIndex].id;
                }
            }
        }
    }
    return { subscriberId, alternateId };
};

/**
 * gets member's address from OBAPI if it exists
 * @param {object} body - OBAPI member profile response
 * @returns string representing member's address
 */
const getInputAddress = (body) => {
    const addresses = body?.demographics?.address;

    if (addresses && addresses.length > 0) {
        let permanentAddressIndex = addresses.findIndex(
            (address) => address.addressType === 'PERMANENT'
        );

        // If the addressType of 'permanent' doesn't exist, or exists but contains no address info, default to first address
        if (
            permanentAddressIndex == -1 ||
            Object.keys(addresses[permanentAddressIndex])?.length == 1
        ) {
            permanentAddressIndex = 0;
        }
        const permanentAddress = addresses[permanentAddressIndex];
        return MapboxLogic.convertOBAPIAddress(permanentAddress);
    } else {
        console.error('Address information missing from member details');
        return '';
    }
};

/**
 * Returns the updated url for member-details endpoint based on microfrontend feature flag
 * if x-epe-features contains a feature flag to overwrite member-details endpoint then identifies the updated url
 */
const getUpdatedUrl = () => {
    let updatedUrl;
    const features = JSON.parse(localStorage.getItem('x-epe-features'));
    // identify if there is a member-details-overwrite feature-flag
    const memberDetailsFeature = features?.value?.find((feature) =>
        feature.id?.includes('member-details-overwrite')
    );

    if (memberDetailsFeature && memberDetailsFeature.enabled == true) {
        // identify the overwriting url from text field
        // example- text: 'url=https://gateway-stage.optum.com/api/dev/ben/cptt/benefit-members/v3'
        updatedUrl = memberDetailsFeature.text?.split('=')[1];
    }

    return updatedUrl;
};

export default getMemberDetails;
